import axios from 'axios';
import { getToken, removeToken } from '../utils/authUtils';
import { API_URL, AUTH_CONFIG } from '../config/constants';
import mockApi from './mockApi';
import { logger } from '../utils/logger';
// Use the API URL from constants.js

// Create an Axios instance
// Only log in development mode
logger.debug('Using API URL:', API_URL);
// Ensure API_URL doesn't have trailing spaces
let cleanApiUrl = API_URL.trim();

// Ensure trailing slash for proper URL concatenation
if (!cleanApiUrl.endsWith('/')) {
  cleanApiUrl = cleanApiUrl + '/';
}

// Log the cleaned URL for debugging
logger.debug('Using cleaned API URL:', cleanApiUrl);
logger.debug('API URL character codes after cleaning:', [...cleanApiUrl].map(c => c.charCodeAt(0)));

const api = axios.create({
  baseURL: cleanApiUrl,
  headers: {
    'Content-Type': 'application/json'},
  // Set timeout to 20 seconds to allow for email sending
  timeout: 20000,
  // Add withCredentials to ensure cookies are sent with requests
  withCredentials: true
});

// Retry logic for authentication requests
const MAX_RETRY_ATTEMPTS = 5; // Increased from 3 to 5
const RETRY_DELAY_MS = 500; // Decreased from 1000 to 500

/**
 * Retry a failed API request with exponential backoff
 * @param {Function} apiCall - Function that returns a promise for the API call
 * @param {number} maxRetries - Maximum number of retry attempts
 * @param {number} initialDelay - Initial delay in milliseconds
 * @param {number} attempt - Current attempt number (internal use)
 * @returns {Promise} - Promise that resolves with the API response
 */
const retryRequest = async (apiCall, maxRetries = MAX_RETRY_ATTEMPTS, initialDelay = RETRY_DELAY_MS, attempt = 1) => {
  try {
    return await apiCall();
  } catch (error) {
    // Only retry network errors, timeouts, or 5xx errors
    const shouldRetry = (
      attempt <= maxRetries && 
      (error.code === 'ECONNABORTED' || 
       error.message.includes('timeout') || 
       error.message.includes('Network Error') ||
       (error.response && error.response.status >= 500))
    );

    // More detailed logging for network errors
    if (error.message.includes('Network Error')) {
      logger.warn(`Network Error (Attempt ${attempt}/${maxRetries}):`, {
        url: apiCall.toString().slice(0, 100) + '...',
        baseURL: cleanApiUrl,
        error: error.message
      });
    }
    
    if (shouldRetry) {
      // Calculate delay with exponential backoff
      const delay = initialDelay * Math.pow(1.5, attempt - 1);
      logger.debug(`API request failed. Retrying (${attempt}/${maxRetries}) after ${delay}ms...`);
      
      // Wait for the delay
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return retryRequest(apiCall, maxRetries, initialDelay, attempt + 1);
    }
    
    // If we shouldn't retry or have exceeded max retries, throw the error
    throw error;
  }
};

// Request Interceptor: Attach JWT token to outgoing requests
api.interceptors.request.use(
  (config) => {
    // Track request for debugging
    if (window.debugAuth) {
      config._debugId = window.debugAuth.trackRequest(config);
    }
    
    // Exclude login endpoints from admin route detection
    const isLoginEndpoint = config.url && (
      config.url.includes('/auth/admin/login') ||
      config.url.includes('auth/admin/login')
    );
    
    // Determine if this is an admin route (but not login)
    const isAdminRoute = !isLoginEndpoint && config.url && (
      config.url.startsWith('admin/') || 
      config.url.startsWith('/admin/') ||
      config.url.includes('/admin/')
    );
    
    // Special logging for contact endpoint
    if (config.url && config.url.includes('contact')) {
      console.log('🔍 Contact endpoint detected:', config.url);
      console.log('Is admin route?', isAdminRoute);
      console.log('Is login endpoint?', isLoginEndpoint);
    }
    
    // Use admin token ONLY for admin routes, otherwise use regular user token
    let token;
    if (isAdminRoute) {
      token = localStorage.getItem('adminToken');
      if (config.url && config.url.includes('contact')) {
        console.log('Using admin token for contact endpoint:', !!token);
      }
    } else if (!isLoginEndpoint) {
      // For non-admin, non-login routes, use regular user token
      token = getToken();
    }
    // For login endpoints, don't add any token
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      if (config.url && config.url.includes('contact')) {
        console.log('Authorization header set:', config.headers.Authorization.substring(0, 30) + '...');
      }
    }
    
    // Normalize URL to prevent double slashes
    if (config.url && config.url.startsWith('/')) {
      config.url = config.url.substring(1);
    }
    
    // Log all requests for debugging (in development only)
    logger.debug(`API Request: ${config.method.toUpperCase()} ${config.url}`, {
      baseURL: config.baseURL,
      fullURL: config.baseURL + config.url,
      headers: config.headers,
      data: config.data,
      isAdminRoute: isAdminRoute,
      usingAdminToken: isAdminRoute && !!token
    });
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flags to track if we've detected the server is down
let isServerDown = false;
let serverDownDetectedAt = null;

// Response Interceptor: Handles global errors, like 401 Unauthorized, 504 Gateway Timeout, etc.
api.interceptors.response.use(
  (response) => {
    // Track successful response
    if (window.debugAuth && response.config._debugId) {
      window.debugAuth.trackResponse(response.config._debugId, response);
    }
    return response;
  },
  (error) => {
    // Track error
    if (window.debugAuth && error.config?._debugId) {
      window.debugAuth.trackError(error.config._debugId, error);
    }
    // Handle specific error cases
    if (!error.response) {
      // Network error or timeout
      // Don't log to console in production or for canceled requests
      if (error.message !== 'canceled') {
        logger.error('Network error or timeout:', error.message);
        logger.debug('Request details:', {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method,
          fullURL: error.config?.baseURL + error.config?.url
        });
      }
      
      // Check specifically for timeout errors
      if (error.message === 'canceled') {
        // Silently handle canceled requests
        error.customMessage = 'Request was canceled';
        error.isCanceled = true;
        // Return a rejected promise with minimal info to avoid console errors
        return Promise.reject({ isCanceled: true });
      } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        error.customMessage = 'Request timed out. Please try again.';
        // Add a flag to identify timeout errors
        error.isTimeout = true;
      } else if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        // Connection refused - backend server is not running
        error.customMessage = 'Backend server is not available. Using offline mode.';
        error.isConnectionRefused = true;
        
        // Set the server down flag and timestamp
        isServerDown = true;
        serverDownDetectedAt = Date.now();
        
        // Log the error but don't use mock API as fallback
        logger.warn('Backend server connection issue:', error.config.url);
        
        // Don't use mockApi as fallback - we want real data
        // Retry the request instead
        if (error.config) {
          const method = error.config.method.toLowerCase();
          const url = error.config.url;
          
          // Retry the request after a short delay
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              logger.debug(`Retrying ${method.toUpperCase()} request to ${url}`);
              // Create a new request with the same config
              api(error.config).then(resolve).catch(reject);
            }, 1000); // 1 second delay before retry
          });
        }
        
        // Implement offline mode with mock responses
        // Check if this is an authentication request
        if (error.config && error.config.url) {
          // For /auth/me endpoint, return success with stored user data
          if (error.config.url.includes('/auth/me')) {
            try {
              const userData = localStorage.getItem('userData');
              if (userData) {
                const user = JSON.parse(userData);
                return Promise.resolve({ 
                  data: { user, success: true },
                  status: 200,
                  statusText: 'OK (Offline Mode)'
                });
              }
            } catch (e) {
              // Silent error, continue with normal error flow
            }
          }
          
          // For login endpoint, use mock authentication
          if (error.config.url.includes('/auth/login')) {
            try {
              // Create a mock response based on the request body
              const requestData = JSON.parse(error.config.data);
              const timestamp = Date.now();
              const mockToken = 'mock-token-' + timestamp;
              const mockUser = {
                id: 'offline-user-' + timestamp,
                userType: requestData.userType || 'candidate',
                profileCompleted: false,
                createdAt: new Date().toISOString()
              };
              
              return Promise.resolve({
                data: { token: mockToken, user: mockUser, success: true },
                status: 200,
                statusText: 'OK (Offline Mode)'
              });
            } catch (e) {
              // Silent error, continue with normal error flow
            }
          }
          
          // For job details endpoint, return mock job data
          if (error.config.url.match(/\/candidate\/jobs\/[\w-]+$/)) {
            try {
              // Extract job ID from URL
              const jobId = error.config.url.split('/').pop();
              
              // Create mock job data
              const mockJobData = {
                job: {
                  id: jobId,
                  title: 'Senior Frontend Developer',
                  company: 'Tech Innovations',
                  logo: 'https://via.placeholder.com/80',
                  location: 'Remote',
                  type: 'Full-time',
                  experienceLevel: 'Senior',
                  salary: '$100,000 - $130,000',
                  postedDate: '3 days ago',
                  description: 'We are looking for a Senior Frontend Developer with expertise in React, TypeScript, and modern CSS frameworks. The ideal candidate will have 5+ years of experience building responsive web applications.',
                  aboutCompany: 'Tech Innovations is a leading technology company specializing in creating cutting-edge web applications for enterprise clients. With offices in major tech hubs and a remote-first culture, we offer a flexible work environment and competitive benefits.',
                  requirements: [
                    'Strong proficiency in JavaScript, including DOM manipulation and the JavaScript object model',
                    'Thorough understanding of React.js and its core principles',
                    'Experience with popular React workflows (Redux, Hooks, etc)',
                    'Familiarity with newer specifications of ECMAScript',
                    'Experience with data structure libraries (e.g., Immutable.js)',
                    'Knowledge of isomorphic React is a plus',
                    'Understanding of server-side rendering',
                    'Experience with GraphQL and Apollo is a plus',
                    'Experience with common front-end development tools such as Babel, Webpack, NPM, etc.',
                    'Ability to understand business requirements and translate them into technical requirements'
                  ],
                  responsibilities: [
                    'Developing new user-facing features using React.js',
                    'Building reusable components and front-end libraries for future use',
                    'Translating designs and wireframes into high-quality code',
                    'Optimizing components for maximum performance across a vast array of web-capable devices and browsers',
                    'Participating in code reviews and providing constructive feedback to other developers',
                    'Collaborating with back-end developers and web designers to improve usability'
                  ],
                  benefits: [
                    'Competitive salary and equity options',
                    'Health, dental, and vision insurance',
                    'Unlimited PTO policy',
                    'Remote-first work environment',
                    '401(k) with company match',
                    'Professional development budget',
                    'Home office stipend'
                  ],
                  companySize: '51-200 employees',
                  industry: 'Information Technology',
                  matchPercentage: 95
                }
              };
              
              return Promise.resolve({
                data: mockJobData,
                status: 200,
                statusText: 'OK (Offline Mode)'
              });
            } catch (e) {
              // Silent error, continue with normal error flow
            }
          }
          
          // For candidate dashboard endpoint, return mock dashboard data
          if (error.config.url.includes('/candidate/dashboard')) {
            try {
              // Get user data from localStorage using AUTH_CONFIG.USER_KEY
              const userData = localStorage.getItem('userData') || localStorage.getItem(AUTH_CONFIG.USER_KEY);
              if (userData) {
                const user = JSON.parse(userData);
                
                // Validate user data
                if (!user || !user.userType || user.userType !== 'candidate') {
                  throw new Error('Invalid user data for candidate dashboard');
                }
                
                // Log offline mode activation
                logger.debug('Candidate dashboard running in offline mode');
                
                // Create mock dashboard data with more complete user information
                const mockDashboardData = {
                  user: {
                    ...user,
                    name: user.name || 'John Doe',
                    title: user.title || 'Software Developer',
                    location: user.location || 'Remote',
                    profilePicture: user.photoURL || 'https://randomuser.me/api/portraits/men/32.jpg',
                    email: user.email || 'candidate@example.com',
                    phone: user.contactNumber || user.phone || '+1234567890',
                    isPhoneVerified: true,
                    profileCompleted: user.profileCompleted === false ? false : true,
                    skills: user.skills || ['JavaScript', 'React', 'HTML', 'CSS'],
                    experience: user.experience || '2+ years',
                    education: user.education || "Bachelor's Degree",
                    offlineMode: true // Flag to indicate offline mode
                  },
                  stats: {
                    applications: 12,
                    interviews: 3,
                    savedJobs: 8,
                    profileViews: 24,
                    lastUpdated: new Date().toISOString()
                  },
                  recommendedJobs: [
                    {
                      id: 'job-1',
                      title: 'Frontend Developer',
                      company: 'Tech Solutions Inc.',
                      location: 'New York, NY',
                      salary: '$80,000 - $100,000',
                      postedDate: '2 days ago',
                      description: 'We are looking for a skilled Frontend Developer to join our team...',
                      skills: ['React', 'JavaScript', 'CSS', 'HTML'],
                      matchScore: 95,
                      applyUrl: '#apply-job-1'
                    },
                    {
                      id: 'job-2',
                      title: 'UX Designer',
                      company: 'Creative Designs',
                      location: 'Remote',
                      salary: '$70,000 - $90,000',
                      postedDate: '1 week ago',
                      description: 'Join our design team to create beautiful user experiences...',
                      skills: ['Figma', 'UI/UX', 'Prototyping', 'User Research'],
                      matchScore: 87,
                      applyUrl: '#apply-job-2'
                    },
                    {
                      id: 'job-3',
                      title: 'Full Stack Developer',
                      company: 'Innovative Apps',
                      location: 'San Francisco, CA',
                      salary: '$100,000 - $130,000',
                      postedDate: '3 days ago',
                      description: 'Looking for a Full Stack Developer with experience in...',
                      skills: ['React', 'Node.js', 'MongoDB', 'Express'],
                      matchScore: 92,
                      applyUrl: '#apply-job-3'
                    }
                  ],
                  savedJobs: [
                    {
                      id: 'saved-1',
                      title: 'Backend Developer',
                      company: 'Data Systems',
                      location: 'Chicago, IL',
                      salary: '$90,000 - $110,000',
                      postedDate: '5 days ago',
                      description: 'Join our backend team to build robust APIs and services...',
                      skills: ['Python', 'Django', 'PostgreSQL', 'Docker'],
                      matchScore: 88,
                      applyUrl: '#apply-saved-1'
                    },
                    {
                      id: 'saved-2',
                      title: 'DevOps Engineer',
                      company: 'Cloud Solutions',
                      location: 'Remote',
                      salary: '$95,000 - $120,000',
                      postedDate: '1 week ago',
                      description: 'Help us build and maintain our cloud infrastructure...',
                      skills: ['AWS', 'Kubernetes', 'CI/CD', 'Terraform'],
                      matchScore: 82,
                      applyUrl: '#apply-saved-2'
                    }
                  ],
                  // Add notification about offline mode
                  offlineNotice: {
                    message: 'You are viewing data in offline mode. Some features may be limited.',
                    timestamp: new Date().toISOString()
                  }
                };
                
                return Promise.resolve({
                  data: mockDashboardData,
                  status: 200,
                  statusText: 'OK (Offline Mode)',
                  headers: {
                    'x-offline-mode': 'true',
                    'x-cached-at': new Date().toISOString()
                  }
                });
              }
            } catch (e) {
              // Log error but continue with normal error flow
              logger.warn('Error creating offline candidate dashboard:', e.message);
            }
          }
          
          // For complete-profile endpoint, simulate profile completion
          if (error.config.url.includes('/auth/complete-profile')) {
            try {
              // Get the profile data from the request
              const profileData = JSON.parse(error.config.data);
              
              // Get existing user data from localStorage
              const userData = localStorage.getItem('userData');
              if (userData) {
                const user = JSON.parse(userData);
                
                // Determine user type - ensure it's explicitly set
                const userType = profileData.userType || user.userType || 
                                (profileData.companyName ? 'employer' : 'candidate');
                
                // Update the user data with the profile information
                const updatedUser = {
                  ...user,
                  ...profileData,
                  userType: userType, // Ensure user type is set
                  profileCompleted: true // Explicitly mark as completed
                };
                
                // Store the updated user data
                localStorage.setItem('userData', JSON.stringify(updatedUser));
                
                // Log the update in development mode
                logger.debug(`Profile completed in offline mode: ${userType}`);
                
                // Return a successful response with the appropriate format based on user type
                if (userType === 'employer') {
                  return Promise.resolve({
                    data: { 
                      employer: updatedUser, 
                      success: true,
                      message: 'Employer profile updated successfully (Offline Mode)'
                    },
                    status: 200,
                    statusText: 'OK (Offline Mode)'
                  });
                } else {
                  return Promise.resolve({
                    data: { 
                      user: updatedUser, 
                      success: true,
                      message: 'Candidate profile updated successfully (Offline Mode)'
                    },
                    status: 200,
                    statusText: 'OK (Offline Mode)'
                  });
                }
              }
            } catch (e) {
              // Silent error, continue with normal error flow
            }
          }
        }
      }
    } else {
      // Server returned an error response
      switch (error.response.status) {
        case 401:
          // Unauthorized - token is invalid or missing
          try {
            logger.error('401 Unauthorized:', {
              url: error.config?.url,
              method: error.config?.method,
              pathname: window.location.pathname
            });
            
            // Check if we're on admin route
            const isAdminRoute = window.location.pathname.startsWith('/admin');
            
            if (isAdminRoute) {
              // Check if admin token exists
              const adminToken = localStorage.getItem('adminToken');
              
              if (!adminToken) {
                // No token found, redirect to login
                logger.warn('Admin 401 - No token found, redirecting to admin login');
                window.location.href = '/admin/login';
                break;
              }
              
              // Token exists but got 401 - token might be expired or invalid
              logger.error('Admin 401 - Token invalid or expired', {
                url: error.config?.url,
                hasToken: !!adminToken
              });
              
              // Only clear and redirect if this is not a one-time error
              // Check if this is a repeated 401
              const last401Time = sessionStorage.getItem('last401Time');
              const now = Date.now();
              
              if (last401Time && (now - parseInt(last401Time)) < 2000) {
                // Repeated 401 within 2 seconds - token is definitely invalid
                logger.warn('Repeated 401 - clearing admin session');
                localStorage.removeItem('adminToken');
                localStorage.removeItem('adminUser');
                sessionStorage.removeItem('last401Time');
                window.location.href = '/admin/login';
              } else {
                // First 401 - just log it and store timestamp
                sessionStorage.setItem('last401Time', now.toString());
                logger.warn('First 401 detected - will clear session if repeated');
              }
              break;
            }
            
            // For user routes - enhanced support/ticket endpoint detection
            const url = error.config?.url || '';
            const isSupportTicket = url.includes('support') || url.includes('ticket') || url.includes('tickets') || url.includes('comment');
            
            // Check token age
            const token = getToken();
            const tokenCreatedAt = localStorage.getItem('authTokenCreatedAt');
            const now = Date.now();
            
            // Check for recent user activity
            const lastUserActivity = sessionStorage.getItem('lastUserActivity');
            const recentActivity = lastUserActivity && (now - parseInt(lastUserActivity)) < 300000; // 5 minutes
            
            // Extended fresh token check (120 seconds instead of 30)
            if (token && tokenCreatedAt && (now - parseInt(tokenCreatedAt)) < 120000) {
              // Token is fresh (< 120 seconds), don't clear
              logger.warn('401 but token is fresh - NOT clearing auth', {
                url,
                tokenAge: now - parseInt(tokenCreatedAt),
                decision: 'preserve_token'
              });
              error.customMessage = 'Unable to authenticate. Please try again.';
              return Promise.reject(error);
            }
            
            // Preserve tokens for support/ticket endpoints
            if (isSupportTicket && token) {
              // Support/ticket endpoint with token - don't clear immediately
              logger.warn('401 on support/ticket endpoint - NOT clearing auth', {
                url,
                isSupportTicket: true,
                decision: 'preserve_token'
              });
              error.customMessage = 'Unable to load tickets. Please refresh the page.';
              return Promise.reject(error);
            }
            
            // Preserve tokens if recent user activity detected
            if (recentActivity && token) {
              logger.warn('401 but recent activity detected - NOT clearing auth', {
                url,
                timeSinceActivity: lastUserActivity ? now - parseInt(lastUserActivity) : 'unknown',
                decision: 'preserve_token'
              });
              error.customMessage = 'Authentication issue. Please try again.';
              return Promise.reject(error);
            }
            
            // Check if we should retry before clearing auth
            const retryAttempted = sessionStorage.getItem('auth_retry_attempted');
            if (!retryAttempted && token) {
              logger.warn('401 - attempting single retry before clearing auth', { url });
              sessionStorage.setItem('auth_retry_attempted', 'true');
              
              // Clear retry flag after 10 seconds to allow future retries
              setTimeout(() => {
                sessionStorage.removeItem('auth_retry_attempted');
              }, 10000);
              
              // Retry the request once
              return api(error.config).catch(retryError => {
                // If retry also fails with 401, proceed with clearing tokens
                if (retryError.response?.status === 401) {
                  logger.error('Retry also failed with 401 - clearing auth', {
                    url,
                    decision: 'clear_tokens'
                  });
                  // Clear auth and redirect
                  removeToken();
                  localStorage.removeItem('userData');
                  localStorage.removeItem('authTokenCreatedAt');
                  sessionStorage.removeItem('lastUserActivity');
                  sessionStorage.removeItem('auth_retry_attempted');
                  
                  const userData = localStorage.getItem('userData');
                  const parsedUserData = userData ? JSON.parse(userData) : null;
                  const userType = parsedUserData?.userType;
                  
                  if (userType) {
                    window.location.href = `/auth/${userType}`;
                  } else if (window.location.pathname.includes('/employer')) {
                    window.location.href = '/auth/employer';
                  } else {
                    window.location.href = '/auth/candidate';
                  }
                }
                return Promise.reject(retryError);
              });
            }
            
            // Clear auth and redirect as last resort
            logger.error('Clearing auth due to 401 - no conditions met to preserve', {
              url,
              tokenAge: tokenCreatedAt ? now - parseInt(tokenCreatedAt) : 'unknown',
              hasRecentActivity: !!recentActivity,
              isSupportTicket,
              retryAttempted: !!retryAttempted,
              decision: 'clear_tokens'
            });
            removeToken();
            localStorage.removeItem('userData');
            localStorage.removeItem('authTokenCreatedAt');
            sessionStorage.removeItem('lastUserActivity');
            sessionStorage.removeItem('auth_retry_attempted');
            
            const userData = localStorage.getItem('userData');
            const parsedUserData = userData ? JSON.parse(userData) : null;
            const userType = parsedUserData?.userType;
            
            if (userType) {
              window.location.href = `/auth/${userType}`;
            } else if (window.location.pathname.includes('/employer')) {
              window.location.href = '/auth/employer';
            } else {
              window.location.href = '/auth/candidate';
            }
          } catch (e) {
            logger.error('Error handling 401 unauthorized:', e);
            window.location.href = '/auth/candidate';
          }
          break;
        case 504:
          // Gateway Timeout
          logger.error('Gateway Timeout:', error.response);
          error.customMessage = 'Server is taking too long to respond. Please try again later.';
          break;
        case 500:
          // Internal Server Error
          logger.error('Server Error:', error.response);
          error.customMessage = 'Server error. Please try again later.';
          break;
        case 429:
          // Too Many Requests
          error.customMessage = 'Too many requests. Please wait a moment and try again.';
          break;
        case 403:
          // Forbidden - likely permissions issue
          error.customMessage = 'You do not have permission to access this resource.';
          break;
        case 404:
          // Not Found
          error.customMessage = 'The requested resource was not found.';
          break;
        case 400: {
          // Bad Request - check if it's an expected error like "already applied"
          const errorMsg = error.response.data?.error || error.response.data?.message || '';
          if (errorMsg.toLowerCase().includes('already applied')) {
            // This is expected behavior, don't log as error
            error.customMessage = errorMsg;
          } else {
            // Other 400 errors should be logged
            error.customMessage = errorMsg || 'Bad request. Please check your input.';
            logger.error(`API Error (400):`, error.response.data);
          }
          break;
        }
        default:
          // Other errors
          error.customMessage = error.response.data?.message || 'An error occurred. Please try again.';
          logger.error(`API Error (${error.response.status}):`, error.response.data);
          break;
      }
    }
    return Promise.reject(error);
  }
);

// Export the API instance directly
export default api;
